/**
 * ACHIEVEMENT POINT REWARD TRACK — milestones on a player's achievement-point TOTAL that pay out REWARD-TYPE
 * ITEMS. A second progression laid over the ladder that already exists: achievements score points (`pointsOf`),
 * the points sum to a score (`totalPoints`), and this file is what that score BUYS.
 *
 * WHY A SEPARATE TRACK AND NOT MORE ACHIEVEMENTS. An achievement rewards one specific thing you did; a milestone
 * rewards the WHOLE of what you have done, so it can carry a prestige cosmetic without any single goal
 * becoming the obvious one to farm. It also gives the ~5,700 points of generated collection ladder a
 * destination — every rung of every species ladder pushes the same bar.
 *
 * ITEMS AND PETALS, NOTHING ELSE — THE RULE THAT SHAPES THE FILE. A tier grants a pot skin, a decor piece
 * MARKED as an achievement reward (`DecorDef.reward`, which simultaneously removes it from the coin shop),
 * and petals — never coins, xp or seed packets. The point of the track is to be the only source of things
 * that cannot be bought; coins or xp would make it an income stream, and every tuning question about it
 * would turn into an economy question. PETALS ARE THE ONE EXCEPTION because their supply here is a FIXED,
 * BUDGETED total (`TRACK_PETAL_BUDGET`, below): the whole track pays one known number over years of play,
 * so it is a slow drip toward one real-money product, not a rate anyone can farm. `milestoneGrant` enforces
 * the rule by DROPPING everything else rather than trusting callers, because the track can also be written
 * by the `pointTrack` tuning key — arbitrary JSON from the server — and the editor's hidden inputs only
 * constrain the one path a human uses.
 *
 * THE REWARD SHAPE IS STILL `AchievementReward`, imported, not redeclared. Achievements DO pay currency, and one
 * shape across both systems is what keeps the two dev editors in sync — a field added for achievements reaches the
 * track for free and `rewardSummary` renders both. The track simply uses a subset of it, which is a narrower
 * contract enforced at the grant, not a different type. A parallel shape here would be the mechanism by
 * which the two quietly diverge.
 *
 * CLAIM-ON-CLICK is the model, and it is the part with real consequences:
 *   • REACHED — the player's score is at or past the threshold. Computed, never stored: it follows the score,
 *     and it can move BACKWARDS if an achievement is disabled or retuned in the dev menu.
 *   • CLAIMABLE — reached and not yet claimed. Nothing is paid out here.
 *   • CLAIMED — the player clicked it. The id goes in the save (`claimedMilestones`) and the grant is applied
 *     ONCE, exactly like an achievement unlock.
 * A claim is therefore PERMANENT and a reach is not. A milestone that is unclaimed waits indefinitely —
 * there is no expiry and no auto-claim — and one already claimed stays claimed even if the score later falls
 * below its threshold. Re-claiming is impossible because `claim` is keyed by the id set, not by the score.
 *
 * IDS ARE STABLE, FOREVER, for the same reason achievement ids are: claimed state is stored against the id.
 * An id that shifts when milestones are re-ordered or re-tuned silently un-claims rewards a player already
 * took — and then re-pays them. The DEFAULT ids below are `pt<threshold>` because that is a memorable
 * convention, NOT because the id is derived from the threshold: re-tuning `pt500` to need 600 points keeps
 * the id `pt500`. Never renumber an id to match a changed threshold.
 *
 * LIVE-TUNABLE through the `pointTrack` tuning key (`setPointTrackOverrides`, installed in main.tsx beside
 * the `achievements` overlay). Unlike that one this key holds the WHOLE ordered list rather than a per-id
 * overlay — the track is a few dozen rungs that are designed as a curve, so an editor publishing it wants to
 * move a threshold and see the rest of the shape, and per-id patches over an ordered list make reordering
 * and deletion awkward for no saving. A malformed or empty key falls back to the source defaults entirely.
 */

import { normaliseReward } from "./achievements";
import type { AchievementReward } from "./achievements";
import { centsToPetals } from "./petals";
import { SUBSCRIBER_PETAL_MULTIPLIER } from "./subscription";
import { shotRewardTrack } from "./shotMode";

/**
 * THE REWARD TRACK IS SWITCHED OFF (29 Sep 2026) — kept, not deleted. With this false, nothing about the track
 * reaches a player: no milestone is ever "waiting" (`qualifiedUnclaimedMilestones` is empty, so every badge and
 * notification count is 0), nothing can be claimed (`claimMilestone` refuses), and App hands no modal the
 * doorway to it. Rewards a player ALREADY claimed stay theirs — a pot skin claimed before the switch is still
 * owned (`skinsFromMilestones` reads the save, not this flag); taking back a cosmetic would be worse than the
 * feature ever was. The dev editor and the `pointTrack` tuning key still work.
 *
 * To turn it back on: set this true. Nothing else changes.
 */
export const REWARD_TRACK_ENABLED = false;
/** Is the reward track live? The flag above — or, in a dev session, one of the smoke test's reward-track
 *  fixtures (sim/shotMode `shotRewardTrack`), so the dormant feature stays tested. */
export function rewardTrackEnabled(): boolean {
  return REWARD_TRACK_ENABLED || shotRewardTrack();
}

/** One rung of the track: a point THRESHOLD and what it pays. */
export interface PointMilestone {
  /** STABLE id. Claimed state is stored against this; see the module header before changing one. */
  id: string;
  /** Achievement points required to REACH this rung. Always ≥ 1 (a 0-point milestone is claimable by a
   *  brand-new player, which makes it a starting gift rather than a milestone — grant those elsewhere). */
  points: number;
  /** Player-facing name of the rung ("Curator"). Optional: `milestoneLabel` falls back to the threshold. */
  name?: string;
  /** What claiming it pays. `potSkin` is DERIVED from the claimed set (safe to recompute); everything else
   *  is GRANTED ONCE on claim — the same split `AchievementReward` documents. */
  reward?: AchievementReward;
  /** Set false to retire a rung without deleting it: it stops being claimable and leaves the track, but any
   *  player who already claimed it keeps the payout and its skin. */
  enabled?: boolean;
}

/** The lowest legal threshold. 0 would make a rung claimable before the player has done anything. */
export const MIN_MILESTONE_POINTS = 1;

// ── THE DEFAULT TRACK'S INPUTS ─────────────────────────────────────────────────────────────────────────────
// Declared ABOVE `DEFAULT_POINT_TRACK` because it is built from them at module load; a `const` below it would
// still be in its temporal dead zone when `buildDefaultTrack` runs.

/** Points between default rungs, and the top one. See "THE CURVE" on `DEFAULT_POINT_TRACK`. */
export const TRACK_STEP = 20;
export const TRACK_CEILING = 3000;

/**
 * THE PETAL BUDGET — what the WHOLE default track pays in petals, all 150 rungs together.
 *
 * ONE $2.99 PRODUCT, OVER YEARS. The budget is `centsToPetals(299)` (≈ 2,062 at today's wheel, sim/petals.ts),
 * so walking the entire track earns about one small shop item. That is deliberately stingy: petals are the
 * premium currency and the track must not become a way round buying them. An average player
 * (reports/point-rate.html) reaches ~960 points at six months, ~1,710 at a year and ~2,670 at the end of
 * YEAR TWO, which with the numbers below lands ≈ 490 petals, ≈ 960 and ≈ 1,750 — under a quarter of a
 * product in the first half-year, and the track is never quite finished.
 *
 * THE BUDGET IS WHAT LANDS, NOT WHAT IS WRITTEN. Only a subscriber can claim a rung (`claimMilestone`), and
 * `grantPetals` multiplies every subscriber grant by SUBSCRIBER_PETAL_MULTIPLIER (3). So every petal on this
 * track arrives tripled, and the per-rung numbers are the budget DIVIDED by the multiplier — a rung reading
 * `petals: 5` pays 15. Sizing the written numbers as the payout would quietly pay three products, not one.
 * If claiming ever stops requiring a subscription, re-size this: a non-subscriber would land a third of it.
 *
 * THE SHAPE is a weight per rung, normalised to the budget:
 *     weight = 1 + PETAL_RAMP · points/TRACK_CEILING + PETAL_EARLY_LIFT · max(0, 1 − points/PETAL_EARLY_POINTS)
 *   • the RAMP makes later rungs pay more — the last is (1 + RAMP)× the unlifted base — because a late rung
 *     takes an average player ~20 days against ~3 early on. It is kept SLOW on purpose: petals per DAY still
 *     fall across the track, since matching the ~6× gap would pour the budget into rungs few players reach.
 *   • the EARLY LIFT tapers to nothing over the first PETAL_EARLY_POINTS (~5 weeks of average play), so a
 *     new player's first rungs visibly pay; it leaves a shallow dip around 300 points before the ramp wins.
 * Each rung rounds to a whole petal (minimum 1), so the realised total sits a few petals off the budget.
 */
export const TRACK_PETAL_BUDGET = centsToPetals(299);

/**
 * THE CEILING ON ONE RUNG'S PETALS, as WRITTEN — 100, so 300 landed at the subscriber multiplier. The defaults
 * pay at most 7 (21 landed); this exists for a live `pointTrack` edit, which is arbitrary JSON. The server's
 * petal burst (server/index.js CURRENCY_GRACE.petals) is sized to cover the default track's whole budget plus
 * the worst day; a rung of a few hundred would push a new member's backlog claim past it, and the excess would
 * be clamped on the next save after the claim screen had shown it. So a tuned rung is clamped HERE, at install,
 * and the editor's Petals box stops at the same number. 300 landed is still well under one shop item (≈ 2,062).
 */
export const MAX_RUNG_PETALS = 100;
export const PETAL_RAMP = 2;
export const PETAL_EARLY_LIFT = 1.5;
export const PETAL_EARLY_POINTS = 300;

/** The ten hand-authored rungs laid over the generated grid. Ids are FROZEN (see "NAMED RUNGS" below) and
 *  every `points` must land on the TRACK_STEP grid, or the rung is silently not generated. */
const NAMED_RUNGS: Array<{ id: string; points: number; name: string; potSkin?: string }> = [
  { id: "pt25",   points: 20,   name: "Sprouting",   potSkin: "midnight" },
  { id: "pt75",   points: 80,   name: "Taking Root" },
  { id: "pt150",  points: 160,  name: "Tended" },
  { id: "pt250",  points: 260,  name: "Cultivator",  potSkin: "porcelain" },
  { id: "pt400",  points: 400,  name: "Green Thumb" },
  { id: "pt600",  points: 600,  name: "Collector",   potSkin: "mossy" },
  { id: "pt1000", points: 1000, name: "Curator",     potSkin: "urn" },
  { id: "pt1500", points: 1500, name: "Bug Catcher" },
  { id: "pt2200", points: 2200, name: "Conservator" },
  { id: "pt3000", points: 3000, name: "Archivist",   potSkin: "pedestal" },
];

/** A rung's share of the petal budget before normalising. See `TRACK_PETAL_BUDGET` for the terms. */
function petalWeight(points: number): number {
  return 1 + PETAL_RAMP * (points / TRACK_CEILING)
    + PETAL_EARLY_LIFT * Math.max(0, 1 - points / PETAL_EARLY_POINTS);
}

/** Build the source-default track: a rung every TRACK_STEP to TRACK_CEILING, each paying its share of the
 *  petal budget (written pre-multiplier), with the named rungs' ids, names and skins laid on top. */
function buildDefaultTrack(): PointMilestone[] {
  const thresholds = intervalThresholds({ step: TRACK_STEP, ceiling: TRACK_CEILING });
  const total = thresholds.reduce((a, p) => a + petalWeight(p), 0);
  const written = TRACK_PETAL_BUDGET / SUBSCRIBER_PETAL_MULTIPLIER;
  return thresholds.map((points) => {
    const reward: AchievementReward = { petals: Math.max(1, Math.round((written * petalWeight(points)) / total)) };
    const named = NAMED_RUNGS.find((n) => n.points === points);
    if (named?.potSkin) reward.potSkin = named.potSkin;
    return named ? { id: named.id, points, name: named.name, reward } : { id: `pt${points}`, points, reward };
  });
}

/**
 * THE SOURCE-DEFAULT TRACK. Ordered by threshold, ascending — `sortedTrack` enforces that on read, so an
 * out-of-order tuning edit still displays as a ladder.
 *
 * THE CURVE: A RUNG EVERY 20 POINTS TO 3,000 — 150 of them, flat-spaced, and both of those numbers are
 * measured rather than chosen by feel. `sim/pointRateSim.ts` models the real achievement roster against two player
 * archetypes and reports when each would cross every tier; `reports/point-rate.html` is the write-up.
 *
 * WHY A FLAT INTERVAL PRODUCES A RISING GAP, which is the part worth understanding before retuning it. The
 * score is paid almost entirely by per-species collection ladders (25/100/500/1k/10k harvested, 10/25/50/
 * 100/250 caught), and their high rungs cost far more play time per point than their low ones. So the same
 * 20 points takes steadily longer to earn as a player goes deeper, WITHOUT the track itself doing anything:
 * for an average player the gap runs ~3.3 days over the first fifth, ~5 days through the middle, and ~19.6
 * days across the last fifth. A variable interval was considered and rejected — it would be a second
 * difficulty curve stacked on one the ladders already provide.
 *
 * WHY THE CAP IS 3,000 AND NOT THE 5,997 CEILING. The cap is doing as much work as the step. 3,000 is where
 * the cheap ladder rungs run out, so it is the region that produces the ~20-day late-track gap; cap at 2,500
 * instead and that slowdown never happens (the gap stays near 8 days). It is also where the simulated curves
 * flatten — an efficient player tops out near 4,800 points and an average one ends YEAR TWO around 2,668, so
 * rungs above 3,000 would be content almost nobody reaches.
 *
 * NAMED RUNGS. Ten of the 150 carry the names and pot skins of the original hand-authored track, snapped to
 * the nearest multiple of 20. THEIR IDS ARE UNCHANGED and must stay that way — `pt25` now sits at 20 points
 * and `pt250` at 260, because claimed state is keyed by the id and renumbering one would re-lock and re-pay
 * a reward a player already took. They are the only hand-typed part of the track (`NAMED_RUNGS`); every
 * other rung is generated as `pt<threshold>` and falls back to "N points" for its label.
 *
 * THE FIVE FIRST-GENERATION SKINS RIDE HERE — though the track is no longer their only source. All five (midnight,
 * porcelain, urn, mossy, pedestal) used to be paid by five specific achievements — first harvest, 25
 * harvests, 150 harvests, 10 species, 8 fish. That was removed deliberately: a cosmetic handed out for one
 * arbitrary goal is a reward for that goal, whereas the same cosmetic on a point rung is a reward for the
 * WHOLE of what a player has done, which is the distinction this track exists to draw. It also means the
 * skins pace the track instead of all arriving in the first hour (three of the five did). Each of the five is
 * now ALSO paid by one later hand-written achievement (achievements.ts THE SKINS ARE DEALT BY PRESTIGE — none
 * on a first-hour goal but the Round Urn), which is what keeps them obtainable while REWARD_TRACK_ENABLED is false.
 *
 * They are spread across the curve rather than stacked at the top, because a track whose cosmetics are all
 * past 2,000 points has nothing to show a new player: `midnight` lands on the FIRST rung (20 points, reached
 * in a day or two) so the track proves immediately that it pays real things, and `pedestal` — the most
 * elaborate body — anchors the 3,000-point completion. Any `potSkin` id put on a rung must exist in POT_SKINS, or the claim silently grants nothing
 * visible; the dev editor flags a broken id on the row (`brokenSkinId`) rather than letting it ship quietly.
 *
 * EVERY RUNG PAYS PETALS — see `TRACK_PETAL_BUDGET` above for how much and why so little. A skin rung
 * carries both.
 *
 * GENERATED, NOT TYPED. `buildDefaultTrack` writes the list from TRACK_STEP / TRACK_CEILING, the named rungs
 * and the petal curve, so retuning the budget or the ramp is a one-constant edit. The output is an ordinary
 * `PointMilestone[]` on the interval grid, so the editor's `applyInterval` at the same step and ceiling
 * returns it unchanged — every threshold already has its rung, and the merge keeps existing rungs whole.
 * (No unit test pins that round trip yet; achievements.test.ts does pin every rung's skin to POT_SKINS.)
 *
 * DECOR ITEMS are grantable on a rung too (`reward.decor`, a DecorId → count map, paid into the owned bag
 * exactly as a shop purchase). None are in the defaults yet — the rungs pay petals and skins — but the dev
 * editor's item browser authors them, and they are the natural home for a reward-only decor piece.
 */
export const DEFAULT_POINT_TRACK: PointMilestone[] = buildDefaultTrack();

// ── THE INTERVAL (EVENLY-SPACED RUNGS) ─────────────────────────────────────────────────────────────────────

/**
 * BUILD A TRACK AT A FIXED INTERVAL — a rung every `step` points, up to `ceiling`. The dev editor's "every X
 * points" control, extracted here because it is arithmetic over the track and the module header's rule is
 * that such arithmetic does not live in a component.
 *
 * WHY AN INTERVAL AT ALL, when thresholds are already free-form. The hand-authored curve doubles early and
 * then steps in round numbers, which is good pacing but makes "add ten more rungs" a manual arithmetic job,
 * and makes the shape impossible to retune as a whole — moving from a 250-point cadence to a 200-point one
 * means retyping every threshold below the top. An interval expresses the cadence ONCE. The generated track
 * is still an ordinary `PointMilestone[]` afterwards: generating is an EDITING ACTION, not a mode, so every
 * rung stays individually editable and nothing downstream needs to know how a threshold was chosen.
 *
 * ── ID STABILITY IS THE WHOLE PROBLEM ──
 *
 * Claimed state is stored against the id (see the module header), so regenerating a track MUST NOT hand a
 * new id to a threshold a player already claimed — that would re-lock the rung AND re-pay it on the next
 * claim. So generation is a MERGE, never a fresh build:
 *
 *   • A generated threshold that ALREADY EXISTS on `existing` keeps that rung entirely — id, name, reward,
 *     enabled flag. It is the same rung; only rungs around it changed.
 *   • A threshold with no existing rung becomes a NEW rung with a fresh `pt<threshold>` id, carrying
 *     `fill` as its reward so a generated ladder is not a row of rungs that pay nothing.
 *   • An existing rung whose threshold is NOT on the new grid is KEPT by default (`prune: false`) and sorted
 *     back into place. Deleting a rung a player may have claimed is the one destructive thing this could do,
 *     so it never happens implicitly — the editor asks first and passes `prune: true` to mean it.
 *
 * The practical consequence: re-running generation with the same step is a no-op, and changing the step
 * preserves every rung whose threshold survives the change. That is what makes the control safe to hold
 * down and experiment with.
 */
export interface IntervalSpec {
  /** Points between rungs. Clamped to ≥ 1 — a step of 0 would generate infinitely. */
  step: number;
  /** Highest threshold to generate up to, inclusive when it lands exactly on a multiple of `step`. */
  ceiling: number;
  /** Where the ladder starts. Defaults to `step` (so a step of 250 starts at 250, not 0 — a 0-point rung is
   *  claimable by a brand-new player, which `MIN_MILESTONE_POINTS` exists to forbid). */
  start?: number;
  /** The reward a NEWLY created rung gets. Existing rungs keep their own. */
  fill?: AchievementReward;
  /** Remove existing rungs that do not land on the new grid. Destructive — see the header. */
  prune?: boolean;
}

/** The ordered thresholds an interval spec describes. Pure arithmetic, exported so the editor can preview
 *  the count ("this makes 24 rungs") before committing to the edit. */
export function intervalThresholds(spec: IntervalSpec): number[] {
  const step = Math.max(1, Math.round(spec.step || 0));
  const start = Math.max(MIN_MILESTONE_POINTS, Math.round(spec.start ?? step));
  const ceiling = Math.round(spec.ceiling || 0);
  const out: number[] = [];
  // Hard cap: a 1-point step against a 6,000-point ceiling is 6,000 rungs, which no editor wants to render
  // and no player wants to walk. Stopping at 500 keeps a mistyped step from locking up the modal.
  for (let p = start; p <= ceiling && out.length < 500; p += step) out.push(p);
  return out;
}

/**
 * Apply an interval to a track. Returns a NEW sorted list; `existing` is never mutated. See `IntervalSpec`
 * for the merge rules — the short version is that existing thresholds keep their rungs and their ids.
 */
export function applyInterval(existing: PointMilestone[], spec: IntervalSpec): PointMilestone[] {
  const want = intervalThresholds(spec);
  const wanted = new Set(want);
  // Index by threshold, keeping the FIRST rung at each — matching `setPointTrackOverrides`'s duplicate rule,
  // so generating over a track with a duplicate threshold resolves it the same way loading would.
  const byPoints = new Map<number, PointMilestone>();
  for (const m of existing) if (!byPoints.has(m.points)) byPoints.set(m.points, m);

  const out: PointMilestone[] = [];
  for (const p of want) {
    const had = byPoints.get(p);
    out.push(had ? { ...had } : { id: freshId(p, existing, out), points: p, reward: spec.fill ? { ...spec.fill } : undefined });
  }
  // Rungs off the grid: dropped only when explicitly pruning, otherwise preserved and re-sorted in.
  if (!spec.prune) {
    for (const m of existing) if (!wanted.has(m.points)) out.push({ ...m });
  }
  return sortedTrack(out);
}

/** A `pt<threshold>` id that is not already taken, by the source track or by rungs generated so far. The
 *  suffix exists only for the collision case (a retired `pt500` still sitting in the track), because two
 *  rungs sharing an id means one of them silently vanishes on load. */
function freshId(points: number, existing: PointMilestone[], made: PointMilestone[]): string {
  const taken = new Set([...existing, ...made].map((m) => m.id));
  const base = `pt${points}`;
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const id = `${base}_${n}`;
    if (!taken.has(id)) return id;
  }
}

// ── LIVE TUNING ────────────────────────────────────────────────────────────────────────────────────────────

/** The effective track: the source defaults until a live `pointTrack` key replaces them. Read through
 *  `pointTrack()`, never directly — the variable is reassigned whenever tuning changes. */
let TRACK: PointMilestone[] = sortedTrack(DEFAULT_POINT_TRACK);

/** Sort a copy of `list` by ascending threshold, stable on ties by the original order, so a track edited out
 *  of order still reads as a ladder. Does not filter: disabled rungs are dropped by `pointTrack`. */
function sortedTrack(list: PointMilestone[]): PointMilestone[] {
  return list.map((m, i) => ({ m, i }))
    .sort((a, b) => (a.m.points - b.m.points) || (a.i - b.i))
    .map(({ m }) => m);
}

/** `r` with its petals held to MAX_RUNG_PETALS (the achievements overlay's `capPetals`, at the track's ceiling). */
function capRungPetals(r: AchievementReward | undefined): AchievementReward | undefined {
  return r?.petals !== undefined && r.petals > MAX_RUNG_PETALS ? { ...r, petals: MAX_RUNG_PETALS } : r;
}

/**
 * Coerce ONE untrusted milestone from the tuning blob, or null if it cannot be salvaged. An entry without a
 * usable id or threshold is DROPPED rather than defaulted: a milestone whose id was mangled is not the same
 * milestone, and inventing one would create a rung nobody authored that immediately pays out.
 */
function normalise(raw: unknown): PointMilestone | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" ? o.id.trim() : "";
  if (!id) return null;
  const pts = typeof o.points === "number" && Number.isFinite(o.points) ? Math.round(o.points) : NaN;
  if (!Number.isFinite(pts)) return null;
  return {
    id,
    points: Math.max(MIN_MILESTONE_POINTS, pts),
    name: typeof o.name === "string" && o.name ? o.name : undefined,
    // The reward is COERCED, not passed through as-authored. It is the same shape the achievements editor writes,
    // arriving from the same untrusted blob, so it goes through the same `normaliseReward` the achievement
    // overlay uses: counts become finite whole numbers or are dropped. `milestoneGrant` then throws away
    // everything but the decor and petals, but the counts it keeps have to be numbers — the grant path adds it straight
    // into the owned bag, where a `"3"` would make the bag's count a string.
    reward: capRungPetals(normaliseReward(o.reward, undefined)),
    enabled: typeof o.enabled === "boolean" ? o.enabled : undefined,
  };
}

/**
 * Install the live track (called at boot + on every tuning refresh; see main.tsx).
 *
 * Accepts the raw `pointTrack` key: either the ordered ARRAY of milestones, or `{ milestones: [...] }` so an
 * editor can publish sibling settings beside the list later without changing the key's shape. Anything else —
 * and an array whose every entry is malformed — leaves the SOURCE DEFAULTS in place, because a track that
 * renders empty looks to a player exactly like one whose rewards have all been taken away.
 *
 * DUPLICATE IDS keep the FIRST occurrence. Claimed state is keyed by id, so two rungs sharing one id would
 * both read as claimed off a single click.
 */
export function setPointTrackOverrides(raw: unknown): void {
  const list = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === "object" && Array.isArray((raw as { milestones?: unknown }).milestones)
        ? (raw as { milestones: unknown[] }).milestones
        : null);
  if (!list) { TRACK = sortedTrack(DEFAULT_POINT_TRACK); return; }
  const out: PointMilestone[] = [];
  const seen = new Set<string>();
  for (const entry of list) {
    const m = normalise(entry);
    if (!m || seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  TRACK = sortedTrack(out.length ? out : DEFAULT_POINT_TRACK);
}

/** The LIVE track a player walks: ascending by threshold, disabled rungs removed. */
export function pointTrack(): PointMilestone[] {
  return TRACK.filter((m) => m.enabled !== false);
}

/** Every rung INCLUDING disabled ones, in order — for the dev editor, and for looking up a rung a player
 *  claimed before it was retired. */
export function allMilestones(): PointMilestone[] { return TRACK; }

/** One rung by id, or undefined. Searches the full list so a retired rung is still resolvable. */
export function milestone(id: string): PointMilestone | undefined {
  return TRACK.find((m) => m.id === id);
}

/** A rung's display label: its name, else the threshold ("400 points"). */
export function milestoneLabel(m: PointMilestone): string {
  return m.name ?? `${m.points.toLocaleString()} points`;
}

// ── CLAIM STATE ────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Where a rung stands for one player. See the module header: `reached` follows the score and can go back
 * down; `claimed` is permanent.
 *
 * `held` IS QUALIFIED-BUT-NOT-YET-CLAIMABLE — the rung a non-subscriber has fully earned, waiting for them.
 * It is deliberately a separate state from `locked` rather than a flag on it, because the two are opposite
 * facts about the player: `locked` means "you have not reached this yet", `held` means "you HAVE, and it is
 * yours the moment you join". Collapsing them would make the UI unable to tell the player the good half,
 * and the tone of this feature is entirely in that distinction. Nothing about `held` is stored: it is
 * `claimable` re-labelled by the subscription gate, so it becomes `claimable` by itself on the day they
 * subscribe (see sim/subscription.ts).
 */
export type MilestoneState = "locked" | "held" | "claimable" | "claimed";

/**
 * One rung's state for a score + claimed set. A CLAIMED rung reports claimed regardless of the score —
 * a retune that lifts a threshold above a player's total must not take back what they already took.
 *
 * `subscriber` defaults TRUE so every existing caller keeps its meaning; pass the real entitlement to get
 * the `held` state. Note the ORDER: claimed is checked first, so a rung claimed while subscribed stays
 * claimed after a lapse — a subscription gates the claim, never the receipt.
 */
export function milestoneState(
  m: PointMilestone, points: number, claimed: ReadonlySet<string>, subscriber = true,
): MilestoneState {
  if (claimed.has(m.id)) return "claimed";
  if (points < m.points) return "locked";
  return subscriber ? "claimable" : "held";
}

/** Every rung the player has QUALIFIED for and not claimed — regardless of subscription. This is the list
 *  that must never shrink for a non-subscriber: it is derived from the score, so it cannot. */
export function qualifiedUnclaimedMilestones(points: number, claimed: ReadonlySet<string>): PointMilestone[] {
  if (!rewardTrackEnabled()) return [];   // switched off: nothing is ever waiting
  return pointTrack().filter((m) => !claimed.has(m.id) && points >= m.points);
}

/** The next rung the player has NOT reached, or undefined once the track is walked out. Drives the "x / y
 *  points to <name>" line; note it is the next UNREACHED rung, not the next unclaimed one, so a player
 *  sitting on unclaimed rewards still sees what is ahead. */
export function nextMilestone(points: number): PointMilestone | undefined {
  return pointTrack().find((m) => points < m.points);
}

/** Progress 0..1 toward `m` from the PREVIOUS rung's threshold — so a filled bar means "this segment done"
 *  rather than "some fraction of a number that starts at 25". The first rung measures from 0. */
export function milestoneProgress(m: PointMilestone, points: number): number {
  const track = pointTrack();
  const i = track.findIndex((x) => x.id === m.id);
  const floor = i > 0 ? (track[i - 1]?.points ?? 0) : 0;
  const span = m.points - floor;
  if (span <= 0) return points >= m.points ? 1 : 0;
  return Math.max(0, Math.min(1, (points - floor) / span));
}

/** The highest threshold on the live track — the denominator for a whole-track progress bar. 0 when the
 *  track is empty. */
export function trackMaxPoints(): number {
  const track = pointTrack();
  return track[track.length - 1]?.points ?? 0;
}

/**
 * CLAIM one rung. Returns the ONE-TIME grant to apply and the new claimed-id list, or null when the claim is
 * not legal — unknown id, disabled rung, not yet reached, or already claimed. The caller applies the grant
 * and writes `claimedMilestones` in the same save, exactly as an achievement unlock does.
 *
 * Returning null rather than throwing is deliberate: a double-click, or a stale button whose rung stopped
 * being claimable after a live retune, is a no-op and not an error. The grant may be null on a LEGAL claim
 * too — a skin-only rung has nothing to pay out — so check the return, not the grant.
 *
 * THE SUBSCRIPTION GATE SITS IN FRONT OF THE EXISTING CHECKS AND CHANGES NOTHING ELSE. A non-subscriber's
 * claim is refused, and refusing it writes nothing — no id is consumed, no "missed" record is kept — so the
 * rung is in exactly the state it was in before, and will be claimable the moment they subscribe. That is
 * the whole of the hold mechanism: the gate is a return, not a mutation. It defaults TRUE so the referee's
 * other rules stay testable on their own, and because a caller that forgets the flag must fail OPEN — the
 * failure mode of accidentally granting a reward is a happy player, and of accidentally withholding one is
 * a paying player who was robbed.
 */
export function claimMilestone(
  id: string,
  points: number,
  claimed: Iterable<string>,
  subscriber = true,
): { grant: Omit<AchievementReward, "potSkin"> | null; claimed: string[] } | null {
  if (!rewardTrackEnabled()) return null;   // switched off: nothing can be claimed
  const have = new Set(claimed);
  const m = milestone(id);
  if (!m || m.enabled === false) return null;
  if (have.has(id)) return null;
  if (points < m.points) return null;
  if (!subscriber) return null;         // HELD, not lost — nothing is written, so nothing is consumed
  have.add(id);
  return { grant: milestoneGrant(m), claimed: [...have] };
}

/** The one-time grant a rung pays (everything except the derived pot skin), or null when there is nothing to
 *  apply. Mirrors `grantOf` in achievements.ts — same shape in, same emptiness test, so the two claim paths
 *  cannot drift on what counts as "nothing". */
export function milestoneGrant(m: PointMilestone): Omit<AchievementReward, "potSkin"> | null {
  const r = m.reward;
  if (!r) return null;
  // ITEMS AND PETALS, ENFORCED HERE. A reward tier grants reward-type ITEMS — the derived pot skin and any
  // decor — plus petals from the track's fixed budget, and never coins, xp or seeds. Those fields are dropped
  // rather than paid, so a rung that still carries coins (authored before this rule, or arriving from a
  // hand-edited tuning blob) cannot quietly pay them.
  //
  // THE CHECK LIVES IN THE SIM, not only in the editor, because the editor is one of two ways a track is
  // written: `setPointTrackOverrides` accepts the whole list from the `pointTrack` tuning key, which is
  // arbitrary JSON from the server. Hiding the inputs in the UI stops an author adding coins; dropping them
  // here stops anything else from doing so. Seeds go too — a seed packet is a consumable payout, closer to
  // currency than to a skin.
  const decor = r.decor && Object.keys(r.decor).length ? { ...r.decor } : undefined;
  const petals = r.petals && r.petals > 0 ? r.petals : undefined;
  if (!decor && !petals) return null;
  return { ...(petals ? { petals } : {}), ...(decor ? { decor } : {}) };
}

/** Pot-skin ids unlocked by CLAIMED milestones (for gating the skin picker). Derived every read, so it is
 *  correct after a retune and nothing about skins needs persisting beyond the claimed ids. Skins come from
 *  claimed rungs only — reaching a rung does not dress your pots, clicking it does. */
export function skinsFromMilestones(claimed: Iterable<string>): Set<string> {
  const skins = new Set<string>();
  for (const id of claimed) {
    const skin = milestone(id)?.reward?.potSkin;
    if (skin) skins.add(skin);
  }
  return skins;
}

// ── THE SLIDING WINDOW ─────────────────────────────────────────────────────────────────────────────────────

/**
 * THE THREE-SQUARE WINDOW the player looks at, and the one piece of this file whose shape is dictated by the
 * UI rather than by the data. It lives here anyway: every case below is arithmetic over the track, it is the
 * part most easily got subtly wrong, and a component that recomputes it inline is one nobody can check.
 *
 * Three rounded squares — left, centre, right — joined by two bars that fill left to right. The window
 * SLIDES, driven by one number: `reached`, how many rungs the score has passed.
 *
 *   reached 0:  [START] --fills--> [rung 0] .......... [rung 1]    left bar MOVES, right bar empty
 *   reached 1:  [START] ==full===> [rung 0] --fills--> [rung 1]    left bar full, right bar moves
 *   reached 2:  [rung 0] ==full==> [rung 1] --fills--> [rung 2]    window has SLID by one
 *   reached n:  [rung n-2] =full=> [rung n-1] -fills-> [rung n]
 *
 * So the CENTRE square is the last rung reached and the RIGHT square is the one being worked toward — except
 * in the START state, where there is nothing behind the player at all and the left square is a synthetic cap
 * (`kind: "start"`) with the bar to the centre carrying the progress. That single exception is why this is a
 * function and not an index expression at the call site.
 *
 * The two bar fills are returned as explicit fields rather than left to the component to work out, so the
 * caller never has to know which case it is in. That is the entire point of returning this shape.
 *
 * TRACK EXHAUSTED: with every rung reached there is no next one, so `right` is empty and `rightFill` is 0.
 * The window stops sliding and shows the last two rungs joined by a full bar — a finished track reads as
 * finished rather than collapsing into an empty row.
 *
 * A SHORT TRACK (one or two rungs in total) yields empty slots for the squares that do not exist. The
 * component still renders three positions, so the geometry never shifts.
 *
 * `reached` COUNTS RUNGS PASSED, NOT RUNGS CLAIMED. A player sitting on ten unclaimed rewards has still
 * walked the track and the window keeps moving ahead of them; the unclaimed ones are caught by the grid
 * below, which is what makes them impossible to lose.
 */
export interface TrackWindowSlot {
  /** "start" is the synthetic cap shown before any rung is reached; "milestone" carries a real rung. */
  kind: "start" | "milestone";
  /** The rung, or null for the START cap and for slots past the end of a short or finished track. */
  milestone: PointMilestone | null;
  /** The rung's state, or null when there is no rung in this slot. */
  state: MilestoneState | null;
}

export interface TrackWindow {
  left: TrackWindowSlot;
  centre: TrackWindowSlot;
  right: TrackWindowSlot;
  /** Fill 0..1 of the bar between left and centre. 1 in every state except the start. */
  leftFill: number;
  /** Fill 0..1 of the bar between centre and right. 0 when the track is exhausted. */
  rightFill: number;
  /** How many rungs the score has passed — the window's slide index, and the cut the grid below is taken
   *  from. Exposed so the component and the grid cannot disagree about where the window sits. */
  reached: number;
}

/** A square the track is too short — or too finished — to fill. */
const EMPTY_SLOT: TrackWindowSlot = { kind: "milestone", milestone: null, state: null };
/** The synthetic cap that stands in for "nothing behind you yet". */
const START_SLOT: TrackWindowSlot = { kind: "start", milestone: null, state: null };

/** One slot for a rung that may not exist. */
function slotFor(
  m: PointMilestone | undefined, points: number, claimed: ReadonlySet<string>, subscriber: boolean,
): TrackWindowSlot {
  return m ? { kind: "milestone", milestone: m, state: milestoneState(m, points, claimed, subscriber) } : EMPTY_SLOT;
}

/** Build the three-square window for a score. See `TrackWindow` for the state table this implements.
 *  `subscriber` only decides whether a qualified rung reads `claimable` or `held` — the window SLIDES the
 *  same either way, because the slide follows the score and a non-subscriber's score is a full score. */
export function trackWindow(points: number, claimed: ReadonlySet<string>, subscriber = true): TrackWindow {
  const track = pointTrack();
  const reached = reachedCount(points);

  // THE START STATE — nothing behind the player, so the left square is the START cap and the bar leading to
  // the centre (the first rung) is the one that moves.
  if (reached === 0) {
    const first = track[0];
    return {
      left: START_SLOT,
      centre: slotFor(first, points, claimed, subscriber),
      right: slotFor(track[1], points, claimed, subscriber),
      leftFill: first ? milestoneProgress(first, points) : 0,
      rightFill: 0,
      reached,
    };
  }

  // THE STEADY STATE — centre is the last rung passed, right is the next one to reach, and left is the rung
  // before centre (still the START cap when exactly one rung has been passed and there is nothing earlier).
  const right = track[reached];
  return {
    left: reached >= 2 ? slotFor(track[reached - 2], points, claimed, subscriber) : START_SLOT,
    centre: slotFor(track[reached - 1], points, claimed, subscriber),
    right: slotFor(right, points, claimed, subscriber),
    // Always full: the player is past the centre rung, so the segment behind it is behind them too.
    leftFill: 1,
    // Measured from the centre rung's threshold, so a full bar means "this segment done" rather than some
    // fraction of an absolute number. 0 once the track is walked out.
    rightFill: right ? milestoneProgress(right, points) : 0,
    reached,
  };
}

/** How many rungs the score has passed. The window's slide index, and the grid's cut. */
export function reachedCount(points: number): number {
  return pointTrack().filter((m) => points >= m.points).length;
}

/**
 * Every rung that has SLID OFF the left of the window — the grid underneath it, claimed and unclaimed both.
 *
 * THIS IS THE SAFETY NET and its contract is that an unclaimed reward is impossible to lose, so it returns
 * everything behind the window with no cap and no recency filter: a player who crossed six rungs at once
 * while the modal was shut finds all six here. Track order, oldest first, so the grid reads the way the
 * track does.
 *
 * A rung is "behind" when it sits before the window's LEFT square, which is index `reached - 2` in the
 * steady state — so the grid holds rungs 0..reached-3. With fewer than three rungs passed nothing has slid
 * off and the grid is empty, which is the correct answer for a new player and is why the START state shows
 * the START square rather than an empty grid.
 *
 * CLAIMED RUNGS STAY, greyed rather than removed: the grid doubles as the record of what the track has
 * already paid, and a row that empties itself as you claim looks like a bug.
 */
export function slidPastMilestones(points: number): PointMilestone[] {
  return pointTrack().slice(0, Math.max(0, reachedCount(points) - 2));
}

/**
 * Rungs claimable from the GRID — reached, unclaimed, and already slid past the window. This is the number
 * the point-track modal's grid heading advertises: "N to claim" for a subscriber, `SUB_COPY.heldCount`'s
 * "N rewards waiting for you" for everyone else. Deliberately EXCLUDES the one or two rungs still visible
 * in the window above the grid — the heading labels the grid, and counting a square the player can already
 * see would make the number disagree with what is under it. Ungated by the subscription, like every other
 * held-reward count: an earned reward is waiting whether or not the player has joined.
 */
export function unclaimedBehindCount(points: number, claimed: ReadonlySet<string>): number {
  return slidPastMilestones(points).filter((m) => !claimed.has(m.id)).length;
}
